import { joinClassNames } from '@vertex-protocol/web-common';
import { Icons, Spinner } from '@vertex-protocol/web-ui';
import { NadoDepositButtonState } from '../types';

interface Props {
  state: NadoDepositButtonState;
  /** Whether this is a cross-chain deposit (3 steps) or same-chain (2 steps) */
  isCrossChain: boolean;
  /** Whether the bridge has been delivered (distinguishes pre-bridge vs post-bridge approve) */
  bridgeDelivered?: boolean;
  /** Bridge provider for dynamic labels */
  bridgeProvider?: 'oft' | 'cctp' | 'across' | 'stargate' | 'none';
  /** Source chain block explorer URL (e.g. "https://etherscan.io") for bridge tx link */
  sourceExplorerUrl?: string;
  /** Bridge tx hash on the source chain */
  bridgeTxHash?: string;
  /** Ink block explorer URL for deposit tx link */
  inkExplorerUrl?: string;
  /** Deposit tx hash on Ink (nado deposit or approval) */
  depositTxHash?: string;
}

type StepStatus = 'pending' | 'active' | 'complete';

interface Step {
  label: string;
  hint?: string;
  status: StepStatus;
  /** Block explorer URL for a completed tx — opens in new tab when clicked */
  explorerUrl?: string;
}

/**
 * Progress tracker for the deposit flow.
 * Cross-chain: Bridge → Approve → Deposit (3 steps)
 * Same-chain: Approve → Deposit (2 steps)
 */
export function NadoDepositProgress({
  state, isCrossChain, bridgeDelivered = false, bridgeProvider = 'oft',
  sourceExplorerUrl, bridgeTxHash, inkExplorerUrl, depositTxHash,
}: Props) {
  if (!shouldShowProgress(state, isCrossChain, bridgeDelivered)) return null;

  const bridgeExplorerLink = bridgeTxHash && sourceExplorerUrl
    ? `${sourceExplorerUrl}/tx/${bridgeTxHash}`
    : undefined;

  const steps = isCrossChain
    ? getCrossChainSteps(state, bridgeDelivered, bridgeProvider, bridgeExplorerLink)
    : getSameChainSteps(state);

  return (
    <div className="bg-surface-2 flex flex-col gap-y-3 rounded-sm p-3">
      {/* Segmented progress bar */}
      <div className="flex gap-x-1">
        {steps.map((step, i) => (
          <div
            key={i}
            className={joinClassNames(
              'h-1 flex-1 rounded-full transition-all duration-500',
              step.status === 'complete'
                ? 'bg-positive'
                : step.status === 'active'
                  ? 'bg-primary'
                  : 'bg-surface-3',
            )}
          />
        ))}
      </div>

      {/* Step labels */}
      <div className="flex flex-col gap-y-1.5">
        {steps.map((step, i) => (
          <div key={i} className="flex items-center gap-x-2">
            <StepIcon status={step.status} />
            <div className="flex flex-col">
              {step.explorerUrl && step.status === 'complete' ? (
                <a
                  href={step.explorerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-positive text-xs underline decoration-positive/40 hover:decoration-positive transition-colors"
                >
                  {step.label} ↗
                </a>
              ) : (
                <span
                  className={joinClassNames(
                    'text-xs',
                    step.status === 'active'
                      ? 'text-text-primary font-medium'
                      : step.status === 'complete'
                        ? 'text-positive'
                        : 'text-text-tertiary',
                  )}
                >
                  {step.label}
                </span>
              )}
              {step.hint && step.status === 'active' && (
                <span className="text-text-tertiary text-2xs">
                  {step.hint}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepIcon({ status }: { status: StepStatus }) {
  if (status === 'complete') {
    return <Icons.CheckCircle className="text-positive size-3.5" />;
  }
  if (status === 'active') {
    return <Spinner className="text-primary size-3.5" />;
  }
  return <div className="bg-surface-3 size-3.5 rounded-full" />;
}

function shouldShowProgress(
  state: NadoDepositButtonState,
  isCrossChain: boolean,
  bridgeDelivered: boolean,
): boolean {
  if (isCrossChain) {
    // Don't show tracker for pre-bridge chain switch (button label is enough)
    if ((state === 'requires_switch_chain' || state === 'switching_chain') && !bridgeDelivered) {
      return false;
    }
    // Show tracker from approve step onwards
    return [
      'requires_approve', 'approving', 'quoting',
      'ready_bridge', 'bridging', 'bridge_submitted', 'bridge_pending',
      'partial_arrival', 'arrival_timeout', 'wallet_mismatch',
      'requires_switch_chain', 'switching_chain',
      'ready_deposit', 'depositing', 'success',
    ].includes(state);
  }
  // Same-chain: show once approve or deposit starts
  return [
    'approving', 'ready_deposit', 'depositing', 'success',
  ].includes(state);
}

// Cross-chain steps — adapts based on bridge provider
function getCrossChainSteps(
  state: NadoDepositButtonState,
  bridgeDelivered: boolean,
  provider: string,
  bridgeExplorerLink?: string,
): Step[] {
  const providerName = provider === 'across' ? 'Across (~2 sec)' : provider === 'cctp' ? 'CCTP (~20–60 min)' : 'LayerZero (~1–3 min)';
  const isAcross = provider === 'across';

  if (isAcross) {
    // Across bundles approve+bridge: 3 steps only
    return [
      { label: 'Bridge to Ink', hint: `via ${providerName}`, status: getBridgeStatus(state, bridgeDelivered), explorerUrl: bridgeExplorerLink },
      { label: 'Approve deposit', hint: 'Requires wallet signature', status: getPostBridgeApproveStatus(state, bridgeDelivered) },
      { label: 'Deposit to account', hint: '~5–10 sec', status: getDepositStatus(state) },
    ];
  }

  // OFT/CCTP/Stargate: 4 steps (separate approve + bridge)
  return [
    { label: 'Approve for bridge', hint: 'Requires wallet signature', status: getPreBridgeApproveStatus(state, bridgeDelivered) },
    { label: 'Bridge to Ink', hint: `via ${providerName}`, status: getBridgeStatus(state, bridgeDelivered), explorerUrl: bridgeExplorerLink },
    { label: 'Approve deposit', hint: 'Requires wallet signature', status: getPostBridgeApproveStatus(state, bridgeDelivered) },
    { label: 'Deposit to account', hint: '~5–10 sec', status: getDepositStatus(state) },
  ];
}

// Same-chain: 2 steps
function getSameChainSteps(state: NadoDepositButtonState): Step[] {
  return [
    { label: 'Approve deposit', hint: 'Requires wallet signature', status: getSameChainApproveStatus(state) },
    { label: 'Deposit to account', hint: '~5–10 sec', status: getSameChainDepositStatus(state) },
  ];
}

function getPreBridgeApproveStatus(state: NadoDepositButtonState, bridgeDelivered: boolean): StepStatus {
  // Pre-bridge chain switch is before approve — approve is still pending
  if (!bridgeDelivered && (state === 'requires_switch_chain' || state === 'switching_chain')) return 'pending';
  if (!bridgeDelivered && (state === 'requires_approve' || state === 'approving')) return 'active';
  if (!bridgeDelivered && state === 'quoting') return 'complete';
  if (['ready_bridge', 'bridging', 'bridge_submitted', 'bridge_pending', 'partial_arrival', 'arrival_timeout'].includes(state)) return 'complete';
  if (bridgeDelivered) return 'complete';
  return 'pending';
}

function getBridgeStatus(state: NadoDepositButtonState, bridgeDelivered: boolean): StepStatus {
  if (['quoting', 'ready_bridge', 'bridging', 'bridge_submitted', 'bridge_pending', 'partial_arrival', 'arrival_timeout'].includes(state)) return 'active';
  // Only complete when bridge has actually delivered
  if (bridgeDelivered) return 'complete';
  if (['ready_deposit', 'depositing', 'success'].includes(state)) return 'complete';
  return 'pending';
}

function getPostBridgeApproveStatus(state: NadoDepositButtonState, bridgeDelivered: boolean): StepStatus {
  if (!bridgeDelivered) return 'pending';
  if (state === 'requires_switch_chain' || state === 'switching_chain') return 'active';
  if (state === 'requires_approve' || state === 'approving') return 'active';
  if (['ready_deposit', 'depositing', 'success'].includes(state)) return 'complete';
  return 'pending';
}

function getDepositStatus(state: NadoDepositButtonState): StepStatus {
  if (state === 'ready_deposit' || state === 'depositing') return 'active';
  if (state === 'success') return 'complete';
  return 'pending';
}

function getSameChainApproveStatus(state: NadoDepositButtonState): StepStatus {
  if (state === 'approving') return 'active';
  if (['ready_deposit', 'depositing', 'success'].includes(state)) return 'complete';
  return 'pending';
}

function getSameChainDepositStatus(state: NadoDepositButtonState): StepStatus {
  if (state === 'ready_deposit' || state === 'depositing') return 'active';
  if (state === 'success') return 'complete';
  return 'pending';
}
